import classnames from 'classnames'

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderSortable,
  TableRow,
  useTableSortWithUrl,
} from '../../../components/Table'
import { Pagination } from '../../../components/pagination'
import { useTablePagination } from '../../../hooks/use-table-pagination'
import { SortDirection } from '../../../types'
import { FILINGS_COLUMNS, VISIBLE_FILINGS_COLUMNS } from '../constants/columns'
import { ColumnId, ParsedFilingItem } from '../constants/types'
import Footer from './Footer'

interface Props {
  data: ParsedFilingItem[]
  /**
   * Whether headers should become sticky
   */
  isHeaderSticky?: boolean
  headerClass?: string
}

export function FilingsTable({ data: filingsData, isHeaderSticky, headerClass }: Props) {
  const { data, sort, changeSort } = useTableSortWithUrl({
    data: filingsData,
    columns: FILINGS_COLUMNS,
    defaultSort: { column: FILINGS_COLUMNS[0], direction: SortDirection.Descending },
  })
  const paginationProps = useTablePagination({ data, shouldResetScroll: true })
  const { paginatedData, ...restPaginationProps } = paginationProps

  return (
    <>
      <Table className="styled-table-new mt-1 table-fixed" cellPadding={1}>
        <TableHead className={classnames(headerClass, { 'sticky z-20 bg-primary': isHeaderSticky })}>
          {VISIBLE_FILINGS_COLUMNS.map((column) => (
            <TableHeaderSortable
              key={column.id}
              align={column.align}
              className={classnames(
                column.className,
                'select-none border-transparent first:border-l first:pl-2.5 last:border-r last:pr-2.5',
                {
                  'w-32': [ColumnId.FilingDate, ColumnId.ReportDate].includes(column.id),
                  'w-24': ColumnId.Form === column.id,
                  'w-20': [ColumnId.Filing, ColumnId.Document].includes(column.id),
                  'w-96': ColumnId.Description === column.id,
                }
              )}
              isActive={column.id === sort.column?.id}
              direction={sort.direction}
              onClick={() => {
                if (column.sortable) changeSort(column)
              }}
              disabled={!column.sortable}
            >
              {column.name}
            </TableHeaderSortable>
          ))}
        </TableHead>
        <TableBody>
          {paginatedData.map((filing, rowIndex) => (
            <TableRow
              key={`tr-${filing.filingDate}-${rowIndex}`}
              className="styled-row is-hover-borders is-bordered is-rounded is-border-top"
            >
              {FILINGS_COLUMNS.map((column, index) => (
                <TableCell
                  key={column.id}
                  isFirst={index === 0}
                  isLast={index === VISIBLE_FILINGS_COLUMNS.length - 1}
                  align={column.align}
                  className="styled-row is-hoverable is-bordered is-border-top is-rounded truncate whitespace-nowrap"
                >
                  {column.renderColumn(filing)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Footer />
      <Pagination
        canGoBack={restPaginationProps.canGoBack}
        goBack={restPaginationProps.goBack}
        canGoForward={restPaginationProps.canGoForward}
        goForward={restPaginationProps.goForward}
        canGoToPage={restPaginationProps.canGoToPage}
        goToPage={restPaginationProps.goToPage}
        visiblePages={restPaginationProps.visiblePages}
        currentPage={restPaginationProps.currentPage}
        className="mb-4"
      />
    </>
  )
}
